// src/projection.ts
function buildIndex(model) {
  const byQualifiedName = /* @__PURE__ */ new Map();
  const byName = /* @__PURE__ */ new Map();
  const viewsByName = /* @__PURE__ */ new Map();
  for (const el of model.allElements) {
    byQualifiedName.set(el.qualifiedName, el);
    byName.set(el.name, [...byName.get(el.name) ?? [], el]);
    if (el.elementType === "view") {
      viewsByName.set(el.name, [...viewsByName.get(el.name) ?? [], el]);
    }
  }
  return {
    byQualifiedName,
    byName,
    viewsByName,
    partDefs: model.allPartDefs,
    relationships: model.allRelationships
  };
}
function resolveFirst(index, ref) {
  const direct = index.byQualifiedName.get(ref);
  if (direct) return direct;
  const byName = index.byName.get(ref);
  if (!byName || byName.length === 0) return void 0;
  if (byName.length === 1) return byName[0];
  return void 0;
}
function resolveView(index, ref) {
  const direct = index.byQualifiedName.get(ref);
  if (direct?.elementType === "view") return direct;
  const candidates = index.viewsByName.get(ref);
  if (!candidates || candidates.length === 0) return void 0;
  if (candidates.length === 1) return candidates[0];
  return void 0;
}
function elementPassesViewFilter(view, element) {
  const filter = view.filter;
  if (!filter) return true;
  const includeUnknowns = Boolean(filter.includeUnknowns);
  const state = element.meta?.state ?? "unknown";
  const confidence = element.meta?.confidence ?? "unknown";
  const kind = element.meta?.kind ?? "?";
  const tags = element.meta?.tags ?? [];
  if (filter.states && filter.states.length > 0) {
    if (state === "unknown" && !includeUnknowns) return false;
    if (state !== "unknown" && !filter.states.includes(state)) return false;
  }
  if (filter.confidence && filter.confidence.length > 0) {
    if (confidence === "unknown" && !includeUnknowns) return false;
    if (confidence !== "unknown" && !filter.confidence.includes(confidence)) return false;
  }
  if (filter.kindsAny && filter.kindsAny.length > 0) {
    if ((kind === "?" || kind === "unknown") && !includeUnknowns) return false;
    if (kind !== "?" && !filter.kindsAny.includes(String(kind))) return false;
  }
  if (filter.tagsAny && filter.tagsAny.length > 0) {
    if (tags.length === 0 && !includeUnknowns) return false;
    if (tags.length > 0 && !tags.some((t) => filter.tagsAny.includes(t))) return false;
  }
  if (element.elementType === "relationship" && filter.relTypesAny && filter.relTypesAny.length > 0) {
    const relType = element.relType ?? "?";
    if ((relType === "?" || relType === "unknown") && !includeUnknowns) return false;
    if (relType !== "?" && !filter.relTypesAny.includes(String(relType))) return false;
  }
  return true;
}
function evaluateViewElements(model, index, view, visiting) {
  if (visiting.has(view.id)) return /* @__PURE__ */ new Set();
  visiting.add(view.id);
  const ids = /* @__PURE__ */ new Set();
  for (const used of view.uses) {
    const usedView = resolveView(index, used);
    if (!usedView) continue;
    for (const id of evaluateViewElements(model, index, usedView, visiting)) ids.add(id);
  }
  const hasWildcard = view.include.some((r) => r.ref === "*");
  if (hasWildcard) {
    for (const el of model.allElements) ids.add(el.id);
  }
  for (const ref of view.include) {
    if (ref.ref === "*" || ref.ref === "?") continue;
    const el = resolveFirst(index, String(ref.ref));
    if (!el) continue;
    ids.add(el.id);
    if (el.elementType === "relationship") {
      const from = el.from.ref !== "?" ? resolveFirst(index, String(el.from.ref)) : void 0;
      const to = el.to.ref !== "?" ? resolveFirst(index, String(el.to.ref)) : void 0;
      if (from) ids.add(from.id);
      if (to) ids.add(to.id);
    }
  }
  for (const ref of view.exclude) {
    if (ref.ref === "*" || ref.ref === "?") continue;
    const el = resolveFirst(index, String(ref.ref));
    if (!el) continue;
    ids.delete(el.id);
  }
  for (const id of [...ids]) {
    const el = model.allElements.find((e) => e.id === id);
    if (!el) {
      ids.delete(id);
      continue;
    }
    if (!elementPassesViewFilter(view, el)) ids.delete(id);
  }
  visiting.delete(view.id);
  return ids;
}
function computeAvailableViewsByElementId(model, index) {
  const map = /* @__PURE__ */ new Map();
  for (const view of model.allViews) {
    const ids = evaluateViewElements(model, index, view, /* @__PURE__ */ new Set());
    for (const id of ids) {
      map.set(id, [...map.get(id) ?? [], view.name]);
    }
  }
  return map;
}
function projectSysmlDiagram(model, options = {}) {
  const index = buildIndex(model);
  if (options.scope) {
    const stateDef = model.allStateDefs.find(
      (sd) => sd.id === options.scope || sd.qualifiedName === options.scope || sd.name === options.scope
    );
    if (stateDef) {
      return projectScopedStateDef(model, index, stateDef);
    }
    return projectScopedBDD(model, index, options.scope);
  }
  const selectedView = options.view ? resolveView(index, options.view) : void 0;
  const view = selectedView ?? {
    id: "sysml:view:$default",
    elementType: "view",
    name: "$default",
    qualifiedName: "$default",
    uses: [],
    include: [{ ref: "*" }],
    exclude: [],
    filter: void 0
  };
  const viewElementIds = evaluateViewElements(model, index, view, /* @__PURE__ */ new Set());
  const availableViewsByElementId = computeAvailableViewsByElementId(model, index);
  const nodes = [];
  const edges = [];
  const includedPartDefs = model.allPartDefs.filter((pd) => viewElementIds.has(pd.id));
  const includedPartDefIds = new Set(includedPartDefs.map((p) => p.id));
  for (const pd of includedPartDefs) {
    nodes.push({
      id: pd.id,
      elementId: pd.id,
      elementType: "partDef",
      label: pd.name,
      source: pd.source,
      availableViews: availableViewsByElementId.get(pd.id) ?? [],
      relatedRelationshipIds: [],
      subElementCount: getSubElementCount(pd)
    });
  }
  const DIAGRAMMABLE_TYPES = /* @__PURE__ */ new Set([
    "requirementDef",
    "actionDef",
    "verificationDef",
    "constraintDef",
    "useCaseDef",
    "interfaceDef",
    "portDef",
    "stateDef",
    "connectionDef",
    "allocationDef",
    "viewDef"
  ]);
  const allNodeElementIds = new Set(includedPartDefIds);
  for (const el of model.allElements) {
    if (!viewElementIds.has(el.id)) continue;
    if (allNodeElementIds.has(el.id)) continue;
    if (!DIAGRAMMABLE_TYPES.has(el.elementType)) continue;
    nodes.push({
      id: el.id,
      elementId: el.id,
      elementType: el.elementType,
      label: el.name,
      source: el.source,
      availableViews: availableViewsByElementId.get(el.id) ?? [],
      relatedRelationshipIds: [],
      subElementCount: getSubElementCount(el)
    });
    allNodeElementIds.add(el.id);
  }
  const nodeByElementId = new Map(nodes.map((n) => [n.elementId, n]));
  for (const pd of includedPartDefs) {
    for (const usage of pd.parts) {
      if (usage.typeRef === "?" || usage.typeRef === "*") continue;
      const target = model.allPartDefs.find((p) => p.qualifiedName === usage.typeRef || p.name === usage.typeRef);
      if (!target || !includedPartDefIds.has(target.id)) continue;
      const label = usage.multiplicity ? `[${usage.multiplicity.lower}..${usage.multiplicity.upper}]` : void 0;
      const edge = {
        id: usage.id,
        relationshipId: usage.id,
        relType: "composition",
        fromElementId: pd.id,
        toElementId: target.id,
        label,
        source: usage.source,
        availableViews: intersectViews(
          availableViewsByElementId.get(pd.id) ?? [],
          availableViewsByElementId.get(target.id) ?? []
        )
      };
      edges.push(edge);
      nodeByElementId.get(pd.id)?.relatedRelationshipIds.push(edge.relationshipId);
      nodeByElementId.get(target.id)?.relatedRelationshipIds.push(edge.relationshipId);
    }
  }
  for (const rel of model.allRelationships) {
    if (!viewElementIds.has(rel.id)) continue;
    if (rel.from.ref === "?" || rel.to.ref === "?") continue;
    const fromEl = resolveFirst(index, String(rel.from.ref));
    const toEl = resolveFirst(index, String(rel.to.ref));
    if (!fromEl || !toEl) continue;
    if (!allNodeElementIds.has(fromEl.id) || !allNodeElementIds.has(toEl.id)) continue;
    const edge = {
      id: rel.id,
      relationshipId: rel.id,
      relType: String(rel.relType),
      fromElementId: fromEl.id,
      toElementId: toEl.id,
      label: rel.name,
      source: rel.source,
      availableViews: availableViewsByElementId.get(rel.id) ?? []
    };
    edges.push(edge);
    nodeByElementId.get(fromEl.id)?.relatedRelationshipIds.push(edge.relationshipId);
    nodeByElementId.get(toEl.id)?.relatedRelationshipIds.push(edge.relationshipId);
  }
  for (const node of nodes) {
    node.relatedRelationshipIds = [...new Set(node.relatedRelationshipIds)];
    node.connectionCount = node.relatedRelationshipIds.length;
  }
  return {
    id: view.id,
    view: { viewId: view.id, viewName: view.name },
    nodes,
    edges
  };
}
function projectScopedBDD(model, index, scopePartDefId) {
  const scopeDef = model.allPartDefs.find(
    (pd) => pd.id === scopePartDefId || pd.qualifiedName === scopePartDefId || pd.name === scopePartDefId
  );
  if (!scopeDef) {
    return { id: "sysml:view:$scoped", view: { viewId: "sysml:view:$scoped", viewName: "$scoped" }, nodes: [], edges: [] };
  }
  const nodes = [];
  const edges = [];
  const childDefIds = /* @__PURE__ */ new Set();
  const childDefs = [];
  for (const usage of scopeDef.parts) {
    if (usage.typeRef === "?" || usage.typeRef === "*") continue;
    const target = model.allPartDefs.find(
      (p) => p.qualifiedName === usage.typeRef || p.name === usage.typeRef
    );
    if (!target || childDefIds.has(target.id)) continue;
    childDefIds.add(target.id);
    childDefs.push(target);
    const label = usage.multiplicity ? `[${usage.multiplicity.lower}..${usage.multiplicity.upper}]` : void 0;
    edges.push({
      id: usage.id,
      relationshipId: usage.id,
      relType: "composition",
      fromElementId: scopeDef.id,
      toElementId: target.id,
      label,
      source: usage.source,
      availableViews: []
    });
  }
  nodes.push({
    id: scopeDef.id,
    elementId: scopeDef.id,
    elementType: "partDef",
    label: scopeDef.name,
    source: scopeDef.source,
    availableViews: [],
    relatedRelationshipIds: edges.map((e) => e.relationshipId),
    subElementCount: getSubElementCount(scopeDef)
  });
  for (const childDef of childDefs) {
    const relIds = edges.filter((e) => e.fromElementId === childDef.id || e.toElementId === childDef.id).map((e) => e.relationshipId);
    nodes.push({
      id: childDef.id,
      elementId: childDef.id,
      elementType: "partDef",
      label: childDef.name,
      source: childDef.source,
      availableViews: [],
      relatedRelationshipIds: relIds,
      subElementCount: getSubElementCount(childDef)
    });
  }
  const allScopedIds = /* @__PURE__ */ new Set([scopeDef.id, ...childDefIds]);
  for (const rel of model.allRelationships) {
    if (rel.from.ref === "?" || rel.to.ref === "?") continue;
    const fromEl = resolveFirst(index, String(rel.from.ref));
    const toEl = resolveFirst(index, String(rel.to.ref));
    if (!fromEl || !toEl) continue;
    if (!allScopedIds.has(fromEl.id) || !allScopedIds.has(toEl.id)) continue;
    edges.push({
      id: rel.id,
      relationshipId: rel.id,
      relType: String(rel.relType),
      fromElementId: fromEl.id,
      toElementId: toEl.id,
      label: rel.name,
      source: rel.source,
      availableViews: []
    });
  }
  for (const node of nodes) {
    const relIds = edges.filter((e) => e.fromElementId === node.elementId || e.toElementId === node.elementId).map((e) => e.relationshipId);
    node.relatedRelationshipIds = [...new Set(relIds)];
    node.connectionCount = node.relatedRelationshipIds.length;
  }
  return {
    id: "sysml:view:$scoped",
    view: { viewId: "sysml:view:$scoped", viewName: scopeDef.name },
    nodes,
    edges
  };
}
function projectScopedStateDef(model, _index, stateDef) {
  const nodes = [];
  const edges = [];
  const states = stateDef.states ?? [];
  const transitions = stateDef.transitions ?? [];
  nodes.push({
    id: stateDef.id,
    elementId: stateDef.id,
    elementType: "stateDef",
    label: stateDef.name,
    source: stateDef.source,
    availableViews: [],
    relatedRelationshipIds: [],
    subElementCount: getSubElementCount(stateDef)
  });
  const stateById = /* @__PURE__ */ new Map();
  for (const state of states) {
    stateById.set(state.name, state.id);
    nodes.push({
      id: state.id,
      elementId: state.id,
      elementType: "stateUsage",
      label: state.name,
      source: state.source,
      availableViews: [],
      relatedRelationshipIds: [],
      subElementCount: 0
    });
    edges.push({
      id: `${stateDef.id}_${state.id}`,
      relationshipId: `${stateDef.id}_${state.id}`,
      relType: "composition",
      fromElementId: stateDef.id,
      toElementId: state.id,
      source: state.source,
      availableViews: []
    });
  }
  for (const tr of transitions) {
    const fromName = String(tr.sourceState?.ref ?? "");
    const toName = String(tr.targetState?.ref ?? "");
    const fromId = stateById.get(fromName);
    const toId = stateById.get(toName);
    if (!fromId || !toId) continue;
    const label = tr.guard ? `[${tr.guard}]` : tr.name || void 0;
    edges.push({
      id: tr.id,
      relationshipId: tr.id,
      relType: "transition",
      fromElementId: fromId,
      toElementId: toId,
      label,
      source: tr.source,
      availableViews: []
    });
  }
  for (const node of nodes) {
    const relIds = edges.filter((e) => e.fromElementId === node.elementId || e.toElementId === node.elementId).map((e) => e.relationshipId);
    node.relatedRelationshipIds = [...new Set(relIds)];
    node.connectionCount = node.relatedRelationshipIds.length;
  }
  return {
    id: "sysml:view:$scoped",
    view: { viewId: "sysml:view:$scoped", viewName: stateDef.name },
    nodes,
    edges
  };
}
function getSubElementCount(element) {
  switch (element.elementType) {
    case "partDef": {
      const pd = element;
      return (pd.parts?.length ?? 0) + (pd.ports?.length ?? 0) + (pd.props?.length ?? 0) + (pd.actions?.length ?? 0);
    }
    case "actionDef": {
      const ad = element;
      return (ad.actions?.length ?? 0) + (ad.flows?.length ?? 0) + (ad.performs?.length ?? 0);
    }
    case "requirementDef": {
      const rd = element;
      return (rd.subrequirements?.length ?? 0) + (rd.constraints?.length ?? 0);
    }
    case "constraintDef": {
      const cd = element;
      return cd.params?.length ?? 0;
    }
    case "stateDef": {
      const sd = element;
      return (sd.states?.length ?? 0) + (sd.transitions?.length ?? 0);
    }
    case "useCaseDef": {
      const ucd = element;
      return (ucd.actors?.length ?? 0) + (ucd.includes?.length ?? 0);
    }
    case "interfaceDef": {
      const ifd = element;
      return (ifd.ends?.length ?? 0) + (ifd.ports?.length ?? 0);
    }
    case "verificationDef": {
      const vd = element;
      return (vd.requirements?.length ?? 0) + (vd.actions?.length ?? 0);
    }
    default:
      return 0;
  }
}
function intersectViews(a, b) {
  if (a.length === 0 || b.length === 0) return [];
  const s = new Set(a);
  return b.filter((x) => s.has(x));
}

// src/ibdProjection.ts
import { DEFAULT_IBD_LAYOUT } from "@arctek/sysml-types";
function findPartDef(model, ref) {
  return model.allPartDefs.find(
    (pd) => pd.id === ref || pd.qualifiedName === ref || pd.name === ref
  );
}
function typeRefToDisplayName(typeRef) {
  if (typeRef === "?" || typeRef === "*") return typeRef;
  const parts = typeRef.split(".");
  return parts[parts.length - 1];
}
function createIBDPorts(ports, ownerId) {
  if (!ports || ports.length === 0) return [];
  return ports.map((port, index) => {
    const side = port.dir === "in" ? "left" : port.dir === "out" ? "right" : "top";
    return {
      id: port.id,
      name: port.name,
      ownerId,
      direction: port.dir,
      typeRef: String(port.typeRef),
      side,
      source: port.source
    };
  });
}
function createIBDParts(parentDef, model, layout) {
  const parts = [];
  const { padding, partSpacing, defaultPartWidth, defaultPartHeight } = layout;
  const partsPerRow = 3;
  let row = 0;
  let col = 0;
  for (const usage of parentDef.parts) {
    const typeDef = usage.typeRef !== "?" ? findPartDef(model, String(usage.typeRef)) : void 0;
    const typePorts = typeDef?.ports ?? [];
    const x = padding + col * (defaultPartWidth + partSpacing);
    const y = padding + row * (defaultPartHeight + partSpacing);
    let multiplicity;
    if (usage.multiplicity) {
      const { lower, upper } = usage.multiplicity;
      multiplicity = lower === upper ? `[${lower}]` : `[${lower}..${upper}]`;
    }
    parts.push({
      id: usage.id,
      name: usage.name,
      typeRef: String(usage.typeRef),
      typeName: typeRefToDisplayName(String(usage.typeRef)),
      multiplicity,
      ports: createIBDPorts(typePorts, usage.id),
      x,
      y,
      width: defaultPartWidth,
      height: defaultPartHeight,
      source: usage.source
    });
    col++;
    if (col >= partsPerRow) {
      col = 0;
      row++;
    }
  }
  return parts;
}
function createIBDConnections(parentDef, parts, parentPorts) {
  const connections = [];
  const relationships = parentDef.relationships ?? [];
  const portMap = /* @__PURE__ */ new Map();
  for (const port of parentPorts) {
    portMap.set(port.id, port);
    portMap.set(port.name, port);
  }
  for (const part of parts) {
    for (const port of part.ports) {
      portMap.set(port.id, port);
      portMap.set(`${part.name}.${port.name}`, port);
    }
  }
  for (const rel of relationships) {
    const relType = String(rel.relType).toLowerCase();
    const isFlow = relType.includes("flow") || relType === "connect" || relType === "binding";
    if (!isFlow) continue;
    const fromRef = rel.fromPort ?? String(rel.from.ref);
    const toRef = rel.toPort ?? String(rel.to.ref);
    const fromPort = portMap.get(fromRef);
    const toPort = portMap.get(toRef);
    if (fromPort && toPort) {
      connections.push({
        id: rel.id,
        fromPortId: fromPort.id,
        toPortId: toPort.id,
        label: rel.name !== rel.id ? rel.name : void 0,
        connectionType: relType === "binding" ? "binding" : "flow",
        source: rel.source
      });
    }
  }
  return connections;
}
function projectIBD(model, partDefId, options = {}) {
  const parentDef = findPartDef(model, partDefId);
  if (!parentDef) return null;
  const layout = {
    ...DEFAULT_IBD_LAYOUT,
    ...options.layout
  };
  const parentPorts = createIBDPorts(parentDef.ports, null);
  const parts = createIBDParts(parentDef, model, layout);
  const connections = createIBDConnections(parentDef, parts, parentPorts);
  return {
    parentId: parentDef.id,
    parentName: parentDef.name,
    parentQualifiedName: parentDef.qualifiedName,
    parentPorts,
    parts,
    connections
  };
}
function getIBDCandidates(model) {
  return model.allPartDefs.filter(
    (pd) => pd.parts.length > 0 || pd.ports && pd.ports.length > 0
  );
}

// src/sequenceProjection.ts
function projectSequenceDiagrams(model, options = {}) {
  const actionDefs = options.actionDef ? model.allActionDefs.filter(
    (ad) => ad.name === options.actionDef || ad.qualifiedName === options.actionDef
  ) : model.allActionDefs;
  const projections = [];
  for (const actionDef of actionDefs) {
    const projection = projectSingleSequence(actionDef, model);
    if (projection) {
      projections.push(projection);
    }
  }
  return projections;
}
function getSequenceCandidates(model) {
  return model.allActionDefs.filter((ad) => hasInteractions(ad));
}
function hasInteractions(actionDef) {
  return (actionDef.flows?.length ?? 0) > 0 || (actionDef.performs?.length ?? 0) > 0 || (actionDef.actions?.length ?? 0) > 0;
}
var nextId = 1;
function seqId(prefix) {
  return `seq_${prefix}_${nextId++}`;
}
function projectSingleSequence(actionDef, model) {
  nextId = 1;
  const lifelines = [];
  const messages = [];
  const lifelineByRef = /* @__PURE__ */ new Map();
  for (const action of actionDef.actions ?? []) {
    const lifeline = createLifelineFromAction(action);
    lifelines.push(lifeline);
    lifelineByRef.set(action.name, lifeline);
    if (action.qualifiedName) {
      lifelineByRef.set(action.qualifiedName, lifeline);
    }
  }
  for (const param of actionDef.params ?? []) {
    const id = seqId("param");
    const lifeline = {
      id,
      elementId: `${actionDef.id}_param_${param.name}`,
      elementType: "actionDef",
      label: param.name,
      source: param.source
    };
    lifelines.push(lifeline);
    lifelineByRef.set(param.name, lifeline);
  }
  for (const flow of actionDef.flows ?? []) {
    ensureLifeline(String(flow.fromRef), lifelines, lifelineByRef, model);
    ensureLifeline(String(flow.toRef), lifelines, lifelineByRef, model);
  }
  let order = 0;
  for (const flow of actionDef.flows ?? []) {
    const fromLifeline = lifelineByRef.get(String(flow.fromRef));
    const toLifeline = lifelineByRef.get(String(flow.toRef));
    if (fromLifeline && toLifeline) {
      const isSelf = fromLifeline.id === toLifeline.id;
      messages.push({
        id: seqId("msg"),
        elementId: flow.id,
        messageType: isSelf ? "self" : "sync",
        fromLifelineId: fromLifeline.id,
        toLifelineId: toLifeline.id,
        label: formatFlowLabel(flow),
        order: order++,
        source: flow.source
      });
    }
  }
  for (const perform of actionDef.performs ?? []) {
    const targetLifeline = lifelineByRef.get(String(perform.ref));
    if (targetLifeline) {
      const contextLifeline = ensureContextLifeline(actionDef, lifelines, lifelineByRef);
      messages.push({
        id: seqId("msg"),
        elementId: perform.id,
        messageType: "sync",
        fromLifelineId: contextLifeline.id,
        toLifelineId: targetLifeline.id,
        label: `perform ${perform.ref}`,
        order: order++,
        source: perform.source
      });
    }
  }
  for (const action of actionDef.actions ?? []) {
    if (action.typeRef && action.typeRef !== "?") {
      const callerLifeline = ensureContextLifeline(actionDef, lifelines, lifelineByRef);
      const calleeLifeline = lifelineByRef.get(action.name);
      if (calleeLifeline && callerLifeline.id !== calleeLifeline.id) {
        messages.push({
          id: seqId("msg"),
          elementId: action.id,
          messageType: "sync",
          fromLifelineId: callerLifeline.id,
          toLifelineId: calleeLifeline.id,
          label: `${action.name}: ${action.typeRef}`,
          order: order++,
          source: action.source
        });
      }
    }
  }
  if (lifelines.length === 0 && messages.length === 0) {
    return null;
  }
  return {
    id: `seq_${actionDef.id}`,
    contextElementId: actionDef.id,
    contextLabel: actionDef.name,
    lifelines,
    messages
  };
}
function createLifelineFromAction(action) {
  return {
    id: seqId("life"),
    elementId: action.id,
    elementType: action.elementType,
    label: action.name,
    source: action.source
  };
}
function ensureLifeline(ref, lifelines, byRef, model) {
  const existing = byRef.get(ref);
  if (existing) return existing;
  const element = model.allElements.find(
    (e) => e.name === ref || e.qualifiedName === ref
  );
  const id = seqId("life");
  const lifeline = {
    id,
    elementId: element?.id ?? ref,
    elementType: element?.elementType ?? "actionDef",
    label: ref,
    source: element?.source
  };
  lifelines.push(lifeline);
  byRef.set(ref, lifeline);
  return lifeline;
}
function ensureContextLifeline(actionDef, lifelines, byRef) {
  const key = `__context_${actionDef.id}`;
  const existing = byRef.get(key);
  if (existing) return existing;
  const id = seqId("ctx");
  const lifeline = {
    id,
    elementId: actionDef.id,
    elementType: "actionDef",
    label: actionDef.name,
    source: actionDef.source
  };
  lifelines.push(lifeline);
  byRef.set(key, lifeline);
  return lifeline;
}
function formatFlowLabel(flow) {
  const item = flow.itemTypeRef && flow.itemTypeRef !== "?" ? String(flow.itemTypeRef) : "";
  return item || "flow";
}

// src/stateMachineProjection.ts
function projectStateMachineDiagrams(model, options = {}) {
  const stateDefs = options.stateDef ? model.allStateDefs.filter(
    (sd) => sd.name === options.stateDef || sd.qualifiedName === options.stateDef
  ) : model.allStateDefs;
  const projections = [];
  for (const stateDef of stateDefs) {
    const projection = projectSingleStateMachine(stateDef);
    if (projection) {
      projections.push(projection);
    }
  }
  return projections;
}
function getStateMachineCandidates(model) {
  return model.allStateDefs.filter((sd) => hasContent(sd));
}
function hasContent(stateDef) {
  return (stateDef.states?.length ?? 0) > 0 || (stateDef.transitions?.length ?? 0) > 0;
}
var nextId2 = 1;
function smId(prefix) {
  return `sm_${prefix}_${nextId2++}`;
}
function projectSingleStateMachine(stateDef) {
  nextId2 = 1;
  const states = [];
  const transitions = [];
  const stateByName = /* @__PURE__ */ new Map();
  for (const stateUsage of stateDef.states ?? []) {
    const node = createStateNode(stateUsage);
    states.push(node);
    stateByName.set(stateUsage.name, node);
    if (stateUsage.qualifiedName) {
      stateByName.set(stateUsage.qualifiedName, node);
    }
  }
  const incomingCount = /* @__PURE__ */ new Map();
  const outgoingCount = /* @__PURE__ */ new Map();
  for (const trans of stateDef.transitions ?? []) {
    const sourceRef = String(trans.sourceState?.ref ?? trans.sourceState);
    const targetRef = String(trans.targetState?.ref ?? trans.targetState);
    const fromNode = stateByName.get(sourceRef);
    const toNode = stateByName.get(targetRef);
    if (fromNode && toNode) {
      transitions.push({
        id: smId("trans"),
        elementId: trans.id,
        fromStateId: fromNode.id,
        toStateId: toNode.id,
        label: trans.guard ? `[${trans.guard}]` : void 0,
        guard: trans.guard,
        source: trans.source
      });
      outgoingCount.set(fromNode.id, (outgoingCount.get(fromNode.id) ?? 0) + 1);
      incomingCount.set(toNode.id, (incomingCount.get(toNode.id) ?? 0) + 1);
    }
  }
  if (states.length > 0) {
    const noIncoming = states.filter((s) => !incomingCount.has(s.id));
    if (noIncoming.length > 0) {
      noIncoming[0].isInitial = true;
    } else {
      states[0].isInitial = true;
    }
    for (const state of states) {
      if (!outgoingCount.has(state.id) && transitions.length > 0) {
        state.isFinal = true;
      }
    }
  }
  if (states.length === 0 && transitions.length === 0) {
    return null;
  }
  return {
    id: `sm_${stateDef.id}`,
    contextElementId: stateDef.id,
    contextLabel: stateDef.name,
    states,
    transitions
  };
}
function createStateNode(stateUsage) {
  return {
    id: smId("state"),
    elementId: stateUsage.id,
    label: stateUsage.name,
    isInitial: false,
    isFinal: false,
    source: stateUsage.source
  };
}

// src/activityProjection.ts
function projectActivityDiagrams(model, options = {}) {
  const actionDefs = options.actionDef ? model.allActionDefs.filter(
    (ad) => ad.name === options.actionDef || ad.qualifiedName === options.actionDef
  ) : model.allActionDefs;
  const projections = [];
  for (const actionDef of actionDefs) {
    const projection = projectSingleActivity(actionDef);
    if (projection) {
      projections.push(projection);
    }
  }
  return projections;
}
function getActivityCandidates(model) {
  return model.allActionDefs.filter((ad) => hasContent2(ad));
}
function hasContent2(actionDef) {
  return (actionDef.actions?.length ?? 0) > 0 || (actionDef.flows?.length ?? 0) > 0 || (actionDef.performs?.length ?? 0) > 0;
}
var nextId3 = 1;
function actId(prefix) {
  return `act_${prefix}_${nextId3++}`;
}
function projectSingleActivity(actionDef) {
  nextId3 = 1;
  const nodes = [];
  const edges = [];
  const nodeByName = /* @__PURE__ */ new Map();
  for (const action of actionDef.actions ?? []) {
    const node = createActionNode(action);
    nodes.push(node);
    nodeByName.set(action.name, node);
    if (action.qualifiedName) {
      nodeByName.set(action.qualifiedName, node);
    }
  }
  for (const perf of actionDef.performs ?? []) {
    const refName = String(perf.ref ?? "");
    if (refName && !nodeByName.has(refName)) {
      const node = {
        id: actId("action"),
        elementId: perf.id,
        nodeType: "action",
        label: refName,
        source: perf.source
      };
      nodes.push(node);
      nodeByName.set(refName, node);
    }
  }
  const incomingCount = /* @__PURE__ */ new Map();
  const outgoingCount = /* @__PURE__ */ new Map();
  for (const flow of actionDef.flows ?? []) {
    const fromRef = resolveRef(flow.fromRef);
    const toRef = resolveRef(flow.toRef);
    const fromNode = nodeByName.get(fromRef);
    const toNode = nodeByName.get(toRef);
    if (fromNode && toNode) {
      const itemLabel = resolveRef(flow.itemTypeRef);
      edges.push({
        id: actId("edge"),
        elementId: flow.id,
        fromNodeId: fromNode.id,
        toNodeId: toNode.id,
        label: itemLabel || void 0,
        source: flow.source
      });
      outgoingCount.set(fromNode.id, (outgoingCount.get(fromNode.id) ?? 0) + 1);
      incomingCount.set(toNode.id, (incomingCount.get(toNode.id) ?? 0) + 1);
    }
  }
  if (edges.length === 0 && nodes.length > 1) {
    for (let i = 0; i < nodes.length - 1; i++) {
      edges.push({
        id: actId("edge"),
        elementId: "",
        fromNodeId: nodes[i].id,
        toNodeId: nodes[i + 1].id
      });
      outgoingCount.set(nodes[i].id, (outgoingCount.get(nodes[i].id) ?? 0) + 1);
      incomingCount.set(nodes[i + 1].id, (incomingCount.get(nodes[i + 1].id) ?? 0) + 1);
    }
  }
  if (nodes.length > 0) {
    const noIncoming = nodes.filter((n) => !incomingCount.has(n.id));
    const initialTarget = noIncoming.length > 0 ? noIncoming[0] : nodes[0];
    const initialNode = {
      id: actId("initial"),
      elementId: "",
      nodeType: "initial",
      label: ""
    };
    nodes.unshift(initialNode);
    edges.push({
      id: actId("edge"),
      elementId: "",
      fromNodeId: initialNode.id,
      toNodeId: initialTarget.id
    });
    const noOutgoing = nodes.filter(
      (n) => n.nodeType === "action" && !outgoingCount.has(n.id)
    );
    if (noOutgoing.length > 0) {
      const finalNode = {
        id: actId("final"),
        elementId: "",
        nodeType: "final",
        label: ""
      };
      nodes.push(finalNode);
      for (const n of noOutgoing) {
        edges.push({
          id: actId("edge"),
          elementId: "",
          fromNodeId: n.id,
          toNodeId: finalNode.id
        });
      }
    }
  }
  for (const node of nodes) {
    if (node.nodeType === "action" && (outgoingCount.get(node.id) ?? 0) > 1) {
      node.nodeType = "decision";
    }
  }
  for (const node of nodes) {
    if (node.nodeType !== "action") continue;
    const outCount = outgoingCount.get(node.id) ?? 0;
    const inCount = incomingCount.get(node.id) ?? 0;
    if (outCount > 1) {
      node.nodeType = "fork";
    } else if (inCount > 1) {
      node.nodeType = "join";
    }
  }
  if (nodes.length === 0 && edges.length === 0) {
    return null;
  }
  return {
    id: `act_${actionDef.id}`,
    contextElementId: actionDef.id,
    contextLabel: actionDef.name,
    activityNodes: nodes,
    activityEdges: edges
  };
}
function createActionNode(action) {
  return {
    id: actId("action"),
    elementId: action.id,
    nodeType: "action",
    label: action.name,
    source: action.source
  };
}
function resolveRef(ref) {
  if (typeof ref === "string") return ref;
  if (ref && typeof ref === "object" && "ref" in ref) {
    return String(ref.ref);
  }
  return String(ref ?? "");
}

// src/packageProjection.ts
function projectPackageDiagrams(model, options = {}) {
  const packages = options.packageName ? model.packages.filter(
    (p) => p.name === options.packageName || p.qualifiedName === options.packageName
  ) : model.packages;
  if (packages.length === 0) return [];
  const nodes = [];
  const edges = [];
  let nextId5 = 1;
  function pkgId(prefix) {
    return `pkg_${prefix}_${nextId5++}`;
  }
  function countElements(pkg) {
    return pkg.partDefs.length + pkg.actionDefs.length + pkg.requirementDefs.length + pkg.constraintDefs.length + pkg.stateDefs.length + pkg.useCaseDefs.length + pkg.connectionDefs.length + pkg.verificationDefs.length + pkg.interfaceDefs.length + pkg.portDefs.length + pkg.itemDefs.length;
  }
  function processPackage(pkg, parentNodeId) {
    const nodeId = pkgId("node");
    nodes.push({
      id: nodeId,
      elementId: pkg.id,
      nodeType: "package",
      label: pkg.name,
      elementCount: countElements(pkg),
      parentId: parentNodeId,
      source: pkg.source
    });
    if (parentNodeId) {
      edges.push({
        id: pkgId("edge"),
        elementId: pkg.id,
        edgeType: "containment",
        fromNodeId: parentNodeId,
        toNodeId: nodeId,
        source: pkg.source
      });
    }
    for (const imp of pkg.imports ?? []) {
      const targetPkg = model.packages.find(
        (p) => p.qualifiedName === imp.path || p.name === imp.path
      );
      if (targetPkg) {
        const targetNode = nodes.find((n) => n.elementId === targetPkg.id);
        if (targetNode) {
          edges.push({
            id: pkgId("edge"),
            elementId: imp.id,
            edgeType: "import",
            fromNodeId: nodeId,
            toNodeId: targetNode.id,
            label: imp.path,
            source: imp.source
          });
        }
      }
    }
    for (const nested of pkg.packages ?? []) {
      processPackage(nested, nodeId);
    }
  }
  for (const pkg of packages) {
    processPackage(pkg);
  }
  if (nodes.length === 0) return [];
  const contextLabel = packages.length === 1 ? packages[0].name : "All Packages";
  return [{
    id: `pkg_${packages[0]?.id ?? "all"}`,
    contextElementId: packages[0]?.id ?? "",
    contextLabel,
    packageNodes: nodes,
    packageEdges: edges
  }];
}
function getPackageCandidates(model) {
  return model.packages;
}

// src/requirementProjection.ts
function projectRequirementDiagrams(model, options = {}) {
  const reqDefs = options.requirementDef ? model.allRequirementDefs.filter(
    (rd) => rd.name === options.requirementDef || rd.qualifiedName === options.requirementDef
  ) : model.allRequirementDefs;
  if (reqDefs.length === 0) return [];
  const nodes = [];
  const edges = [];
  const nodeById = /* @__PURE__ */ new Map();
  let nextId5 = 1;
  function reqId(prefix) {
    return `req_${prefix}_${nextId5++}`;
  }
  const vcByReq = /* @__PURE__ */ new Map();
  for (const vd of model.allVerificationDefs) {
    const subRef = String(vd.subject?.ref ?? "");
    if (subRef) {
      const arr = vcByReq.get(subRef) ?? [];
      arr.push(vd);
      vcByReq.set(subRef, arr);
    }
    for (const rr of vd.requirements ?? []) {
      const ref = String(rr.ref ?? "");
      if (ref) {
        const arr = vcByReq.get(ref) ?? [];
        arr.push(vd);
        vcByReq.set(ref, arr);
      }
    }
  }
  function computeVerificationStatus(linked) {
    if (linked.length === 0) return "no-coverage";
    const allDone = linked.every((vc) => (vc.meta?.state ?? "draft") === "done");
    if (allDone) return "verified";
    const anyDone = linked.some((vc) => (vc.meta?.state ?? "draft") === "done");
    return anyDone ? "partial" : "unverified";
  }
  for (const rd of reqDefs) {
    const linked = [
      ...vcByReq.get(rd.id) ?? [],
      ...vcByReq.get(rd.name) ?? [],
      ...vcByReq.get(rd.qualifiedName) ?? []
    ];
    const uniqueVcs = [...new Map(linked.map((v) => [v.id, v])).values()];
    const constraintSummaries = (rd.constraints ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      expression: c.expression
    }));
    const subreqSummaries = (rd.subrequirements ?? []).map((s) => ({
      id: s.id,
      name: s.name,
      text: s.text
    }));
    const verificationSummaries = uniqueVcs.map((vc) => ({
      id: vc.id,
      name: vc.name,
      method: vc.meta?.kind,
      state: vc.meta?.state
    }));
    const node = {
      id: reqId("node"),
      elementId: rd.id,
      nodeType: "requirement",
      label: rd.name,
      text: rd.text,
      stereotype: "requirement",
      source: rd.source,
      verificationStatus: computeVerificationStatus(uniqueVcs),
      lifecycleState: rd.meta?.state,
      hasConstraints: constraintSummaries.length > 0,
      constraintCount: constraintSummaries.length,
      hasRisks: (rd.meta?.risks?.length ?? 0) > 0,
      riskCount: rd.meta?.risks?.length ?? 0,
      hasQuestions: (rd.meta?.questions?.length ?? 0) > 0,
      questionCount: rd.meta?.questions?.length ?? 0,
      kind: rd.meta?.kind,
      confidence: rd.meta?.confidence,
      subrequirementCount: subreqSummaries.length,
      fullText: rd.text,
      constraints: constraintSummaries,
      verifications: verificationSummaries,
      subrequirements: subreqSummaries,
      notes: rd.meta?.notes,
      tags: rd.meta?.tags,
      risks: rd.meta?.risks,
      questions: rd.meta?.questions
    };
    nodes.push(node);
    nodeById.set(rd.id, node);
    nodeById.set(rd.qualifiedName, node);
    nodeById.set(rd.name, node);
  }
  for (const sat of model.allSatisfies) {
    const reqRef = String(sat.requirement?.ref ?? "");
    const byRef = String(sat.by?.ref ?? "");
    if (!reqRef || !byRef) continue;
    const reqNode = nodeById.get(reqRef);
    if (!reqNode) continue;
    let partNode = nodeById.get(byRef);
    if (!partNode) {
      const partEl = model.allElements.find(
        (e) => e.id === byRef || e.qualifiedName === byRef || e.name === byRef
      );
      if (!partEl) continue;
      partNode = {
        id: reqId("node"),
        elementId: partEl.id,
        nodeType: "part",
        label: partEl.name,
        stereotype: partEl.elementType,
        source: partEl.source,
        lifecycleState: partEl.meta?.state
      };
      nodes.push(partNode);
      nodeById.set(partEl.id, partNode);
      nodeById.set(partEl.qualifiedName, partNode);
      nodeById.set(partEl.name, partNode);
    }
    edges.push({
      id: reqId("edge"),
      elementId: sat.id,
      edgeType: "satisfy",
      fromNodeId: partNode.id,
      toNodeId: reqNode.id,
      label: "satisfy",
      source: sat.source
    });
  }
  for (const vd of model.allVerificationDefs) {
    for (const reqRef of vd.requirements ?? []) {
      const ref = String(reqRef.ref ?? "");
      if (!ref) continue;
      const reqNode = nodeById.get(ref);
      if (!reqNode) continue;
      let verNode = nodeById.get(vd.id);
      if (!verNode) {
        verNode = {
          id: reqId("node"),
          elementId: vd.id,
          nodeType: "verification",
          label: vd.name,
          stereotype: "verify",
          source: vd.source,
          lifecycleState: vd.meta?.state,
          kind: vd.meta?.kind
        };
        nodes.push(verNode);
        nodeById.set(vd.id, verNode);
        nodeById.set(vd.qualifiedName, verNode);
        nodeById.set(vd.name, verNode);
      }
      edges.push({
        id: reqId("edge"),
        elementId: vd.id,
        edgeType: "verify",
        fromNodeId: verNode.id,
        toNodeId: reqNode.id,
        label: "verify",
        source: vd.source
      });
    }
  }
  for (const rd of reqDefs) {
    const parentNode = nodeById.get(rd.id);
    if (!parentNode) continue;
    for (const sub of rd.subrequirements ?? []) {
      const subRef = String(sub.typeRef ?? sub.name ?? "");
      const subNode = nodeById.get(subRef) ?? nodeById.get(sub.name);
      if (subNode) {
        edges.push({
          id: reqId("edge"),
          elementId: sub.id,
          edgeType: "containment",
          fromNodeId: parentNode.id,
          toNodeId: subNode.id,
          source: sub.source
        });
      }
    }
  }
  if (nodes.length === 0) return [];
  const contextLabel = reqDefs.length === 1 ? reqDefs[0].name : "Requirements";
  return [{
    id: `req_${reqDefs[0]?.id ?? "all"}`,
    contextElementId: reqDefs[0]?.id ?? "",
    contextLabel,
    requirementNodes: nodes,
    requirementEdges: edges
  }];
}
function getRequirementCandidates(model) {
  return model.allRequirementDefs;
}

// src/useCaseProjection.ts
function projectUseCaseDiagrams(model, options = {}) {
  const useCaseDefs = options.useCaseDef ? model.allUseCaseDefs.filter(
    (ucd) => ucd.name === options.useCaseDef || ucd.qualifiedName === options.useCaseDef
  ) : model.allUseCaseDefs;
  if (useCaseDefs.length === 0) return [];
  const nodes = [];
  const edges = [];
  const nodeByRef = /* @__PURE__ */ new Map();
  let nextId5 = 1;
  function ucId(prefix) {
    return `uc_${prefix}_${nextId5++}`;
  }
  const actorNames = /* @__PURE__ */ new Set();
  const subjectNames = /* @__PURE__ */ new Set();
  for (const ucd of useCaseDefs) {
    if (ucd.subject) {
      const subRef = String(ucd.subject.ref ?? "");
      if (subRef) subjectNames.add(subRef);
    }
    for (const actor of ucd.actors ?? []) {
      actorNames.add(actor.name);
    }
  }
  for (const subName of subjectNames) {
    const node = {
      id: ucId("boundary"),
      elementId: `boundary_${subName}`,
      nodeType: "system-boundary",
      label: subName
    };
    nodes.push(node);
    nodeByRef.set(`boundary:${subName}`, node);
  }
  for (const actorName of actorNames) {
    const actorUsage = useCaseDefs.flatMap((ucd) => ucd.actors ?? []).find((a) => a.name === actorName);
    const node = {
      id: ucId("actor"),
      elementId: actorUsage?.id ?? `actor_${actorName}`,
      nodeType: "actor",
      label: actorName,
      source: actorUsage?.source
    };
    nodes.push(node);
    nodeByRef.set(actorName, node);
  }
  for (const ucd of useCaseDefs) {
    const subRef = ucd.subject ? String(ucd.subject.ref ?? "") : "";
    const boundaryNode = subRef ? nodeByRef.get(`boundary:${subRef}`) : void 0;
    const node = {
      id: ucId("usecase"),
      elementId: ucd.id,
      nodeType: "usecase",
      label: ucd.name,
      objective: ucd.objective,
      boundaryId: boundaryNode?.id,
      source: ucd.source
    };
    nodes.push(node);
    nodeByRef.set(ucd.id, node);
    nodeByRef.set(ucd.qualifiedName, node);
    nodeByRef.set(ucd.name, node);
  }
  for (const ucd of useCaseDefs) {
    const ucNode = nodeByRef.get(ucd.id);
    if (!ucNode) continue;
    for (const actor of ucd.actors ?? []) {
      const actorNode = nodeByRef.get(actor.name);
      if (!actorNode) continue;
      edges.push({
        id: ucId("edge"),
        elementId: actor.id,
        edgeType: "association",
        fromNodeId: actorNode.id,
        toNodeId: ucNode.id,
        source: actor.source
      });
    }
  }
  for (const ucd of useCaseDefs) {
    const ucNode = nodeByRef.get(ucd.id);
    if (!ucNode) continue;
    for (const inc of ucd.includes ?? []) {
      const ref = String(inc.useCaseRef?.ref ?? "");
      const targetNode = nodeByRef.get(ref);
      if (!targetNode) continue;
      edges.push({
        id: ucId("edge"),
        elementId: inc.id,
        edgeType: "include",
        fromNodeId: ucNode.id,
        toNodeId: targetNode.id,
        label: "include",
        source: inc.source
      });
    }
  }
  for (const ucd of useCaseDefs) {
    if (!ucd.supertype || ucd.supertype === "?") continue;
    const ucNode = nodeByRef.get(ucd.id);
    const superNode = nodeByRef.get(String(ucd.supertype));
    if (ucNode && superNode) {
      edges.push({
        id: ucId("edge"),
        elementId: ucd.id,
        edgeType: "generalization",
        fromNodeId: ucNode.id,
        toNodeId: superNode.id,
        source: ucd.source
      });
    }
  }
  if (nodes.length === 0) return [];
  const contextLabel = useCaseDefs.length === 1 ? useCaseDefs[0].name : "Use Cases";
  return [{
    id: `uc_${useCaseDefs[0]?.id ?? "all"}`,
    contextElementId: useCaseDefs[0]?.id ?? "",
    contextLabel,
    useCaseNodes: nodes,
    useCaseEdges: edges
  }];
}
function getUseCaseCandidates(model) {
  return model.allUseCaseDefs;
}

// src/parametricProjection.ts
function projectParametricDiagrams(model, options = {}) {
  const constraintDefs = options.constraintDef ? model.allConstraintDefs.filter(
    (cd) => cd.name === options.constraintDef || cd.qualifiedName === options.constraintDef
  ) : model.allConstraintDefs;
  const projections = [];
  for (const cd of constraintDefs) {
    const projection = projectSingleParametric(cd, model);
    if (projection) {
      projections.push(projection);
    }
  }
  return projections;
}
function getParametricCandidates(model) {
  return model.allConstraintDefs.filter((cd) => hasContent3(cd));
}
function hasContent3(cd) {
  return (cd.params?.length ?? 0) > 0 || !!cd.expression;
}
var nextId4 = 1;
function paramId(prefix) {
  return `par_${prefix}_${nextId4++}`;
}
function projectSingleParametric(cd, model) {
  nextId4 = 1;
  const constraintNodes = [];
  const valueNodes = [];
  const bindings = [];
  const paramNames = (cd.params ?? []).map((p) => p.name);
  const constraintNode = {
    id: paramId("cblock"),
    elementId: cd.id,
    label: cd.name,
    expression: cd.expression,
    params: paramNames,
    source: cd.source
  };
  constraintNodes.push(constraintNode);
  for (const param of cd.params ?? []) {
    const valueNode = {
      id: paramId("val"),
      elementId: param.name,
      // params don't have their own IDs in the IR
      label: param.name,
      typeRef: typeof param.typeRef === "string" ? param.typeRef : void 0,
      source: param.source
    };
    valueNodes.push(valueNode);
    bindings.push({
      id: paramId("bind"),
      elementId: cd.id,
      constraintNodeId: constraintNode.id,
      paramName: param.name,
      valueNodeId: valueNode.id,
      source: param.source
    });
  }
  if (constraintNodes.length === 0 && valueNodes.length === 0) {
    return null;
  }
  return {
    id: `par_${cd.id}`,
    contextElementId: cd.id,
    contextLabel: cd.name,
    constraintNodes,
    valueNodes,
    bindings
  };
}

// src/elkLayout.ts
var DEFAULT_LAYOUT_OPTIONS = {
  algorithm: "layered",
  direction: "RIGHT",
  nodeSpacing: 40,
  layerSpacing: 60,
  edgeRouting: "ORTHOGONAL"
};
var DEFAULT_NODE_WIDTH = 200;
var DEFAULT_NODE_HEIGHT = 90;
var _elkConstructor = null;
var _elkLoadAttempted = false;
async function loadElk() {
  if (_elkLoadAttempted) return _elkConstructor;
  _elkLoadAttempted = true;
  try {
    const mod = await import("elkjs/lib/elk.bundled.js");
    _elkConstructor = mod.default ?? mod;
  } catch {
  }
  return _elkConstructor;
}
var ALGORITHM_MAP = {
  layered: "org.eclipse.elk.layered",
  stress: "org.eclipse.elk.stress",
  mrtree: "org.eclipse.elk.mrtree",
  force: "org.eclipse.elk.force"
};
function toElkLayoutOptions(opts) {
  return {
    "elk.algorithm": ALGORITHM_MAP[opts.algorithm] ?? ALGORITHM_MAP.layered,
    "elk.direction": opts.direction,
    "elk.spacing.nodeNode": String(opts.nodeSpacing),
    "elk.layered.spacing.nodeNodeBetweenLayers": String(opts.layerSpacing),
    "elk.spacing.edgeNode": String(Math.round(opts.nodeSpacing * 0.5)),
    "elk.edgeRouting": opts.edgeRouting,
    // Ensure sections are generated so we can extract bend points
    "elk.layered.edgeRouting.selfLoopDistribution": "EQUALLY"
  };
}
function toElkGraph(projection, elkOptions) {
  const children = projection.nodes.map((node) => ({
    id: node.id,
    width: DEFAULT_NODE_WIDTH,
    height: DEFAULT_NODE_HEIGHT
  }));
  const edges = projection.edges.map((edge) => ({
    id: edge.id,
    sources: [edge.fromElementId],
    targets: [edge.toElementId]
  }));
  return {
    id: "root",
    layoutOptions: elkOptions,
    children,
    edges
  };
}
function extractNodes(elkResult) {
  if (!elkResult.children) return [];
  return elkResult.children.map((child) => ({
    id: child.id,
    x: child.x ?? 0,
    y: child.y ?? 0,
    width: child.width ?? DEFAULT_NODE_WIDTH,
    height: child.height ?? DEFAULT_NODE_HEIGHT
  }));
}
function extractEdges(elkResult) {
  if (!elkResult.edges) return [];
  return elkResult.edges.map((edge) => {
    const sections = (edge.sections ?? []).map((sec) => ({
      startPoint: { x: sec.startPoint.x, y: sec.startPoint.y },
      endPoint: { x: sec.endPoint.x, y: sec.endPoint.y },
      bendPoints: (sec.bendPoints ?? []).map((bp) => ({ x: bp.x, y: bp.y }))
    }));
    return {
      id: edge.id,
      sections
    };
  });
}
async function computeElkLayout(projection, options) {
  const ELK = await loadElk();
  if (!ELK) throw new Error("elkjs is not available");
  const opts = { ...DEFAULT_LAYOUT_OPTIONS, ...options };
  const elk = new ELK();
  const elkOptions = toElkLayoutOptions(opts);
  const graph = toElkGraph(projection, elkOptions);
  const result = await elk.layout(graph);
  return {
    nodes: extractNodes(result),
    edges: extractEdges(result)
  };
}
function createElkLayout(elkArgs) {
  let elkInstance = null;
  return async function layoutWithElk(projection, options) {
    if (!elkInstance) {
      const ELK = await loadElk();
      if (!ELK) throw new Error("elkjs is not available");
      elkInstance = new ELK(elkArgs);
    }
    const opts = { ...DEFAULT_LAYOUT_OPTIONS, ...options };
    const elkOptions = toElkLayoutOptions(opts);
    const graph = toElkGraph(projection, elkOptions);
    const result = await elkInstance.layout(graph);
    return {
      nodes: extractNodes(result),
      edges: extractEdges(result)
    };
  };
}

// src/edgeRouting.ts
var DEFAULT_CORNER_RADIUS = 6;
function edgeSectionsToSvgPath(sections, cornerRadius = DEFAULT_CORNER_RADIUS) {
  if (sections.length === 0) return "";
  const parts = [];
  for (let si = 0; si < sections.length; si++) {
    const section = sections[si];
    const points = [
      section.startPoint,
      ...section.bendPoints,
      section.endPoint
    ];
    if (points.length < 2) continue;
    if (si === 0) {
      parts.push(`M ${points[0].x} ${points[0].y}`);
    } else {
      parts.push(`L ${points[0].x} ${points[0].y}`);
    }
    if (points.length === 2) {
      parts.push(`L ${points[1].x} ${points[1].y}`);
      continue;
    }
    for (let i = 1; i < points.length - 1; i++) {
      const prev = points[i - 1];
      const curr = points[i];
      const next = points[i + 1];
      const dxIn = prev.x - curr.x;
      const dyIn = prev.y - curr.y;
      const dxOut = next.x - curr.x;
      const dyOut = next.y - curr.y;
      const lenIn = Math.sqrt(dxIn * dxIn + dyIn * dyIn);
      const lenOut = Math.sqrt(dxOut * dxOut + dyOut * dyOut);
      const r = Math.min(cornerRadius, lenIn / 2, lenOut / 2);
      if (r < 0.5) {
        parts.push(`L ${curr.x} ${curr.y}`);
        continue;
      }
      const arcStartX = curr.x + dxIn / lenIn * r;
      const arcStartY = curr.y + dyIn / lenIn * r;
      const arcEndX = curr.x + dxOut / lenOut * r;
      const arcEndY = curr.y + dyOut / lenOut * r;
      const cross = dxIn * dyOut - dyIn * dxOut;
      const sweepFlag = cross > 0 ? 0 : 1;
      parts.push(`L ${arcStartX} ${arcStartY}`);
      parts.push(`A ${r} ${r} 0 0 ${sweepFlag} ${arcEndX} ${arcEndY}`);
    }
    const lastPt = points[points.length - 1];
    parts.push(`L ${lastPt.x} ${lastPt.y}`);
  }
  return parts.join(" ");
}
function getEdgeLabelPosition(sections) {
  if (sections.length === 0) return { x: 0, y: 0 };
  let bestMid = { x: 0, y: 0 };
  let bestLen = -1;
  for (const section of sections) {
    const points = [
      section.startPoint,
      ...section.bendPoints,
      section.endPoint
    ];
    for (let i = 0; i < points.length - 1; i++) {
      const a = points[i];
      const b = points[i + 1];
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      const len = Math.sqrt(dx * dx + dy * dy);
      if (len > bestLen) {
        bestLen = len;
        bestMid = {
          x: (a.x + b.x) / 2,
          y: (a.y + b.y) / 2
        };
      }
    }
  }
  return bestMid;
}

// src/views.ts
var STORAGE_KEY = "sysml.queryViews.v1";
function createMemoryStorage() {
  const map = /* @__PURE__ */ new Map();
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value);
    },
    removeItem: (key) => {
      map.delete(key);
    }
  };
}
var fallbackStorage = createMemoryStorage();
function getDefaultStorage() {
  try {
    const ls = globalThis?.localStorage;
    if (!ls?.getItem || !ls?.setItem || !ls?.removeItem) return fallbackStorage;
    return {
      getItem: (key) => ls.getItem(key),
      setItem: (key, value) => ls.setItem(key, value),
      removeItem: (key) => ls.removeItem(key)
    };
  } catch {
    return fallbackStorage;
  }
}
function safeParseViews(raw) {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(Boolean).filter((v) => typeof v === "object").map((v) => ({
      id: String(v.id ?? ""),
      name: String(v.name ?? ""),
      query: String(v.query ?? ""),
      createdAt: String(v.createdAt ?? ""),
      updatedAt: String(v.updatedAt ?? "")
    })).filter((v) => v.id && v.name);
  } catch {
    return [];
  }
}
function writeViews(storage, views) {
  storage.setItem(STORAGE_KEY, JSON.stringify(views));
}
function createId() {
  try {
    const cryptoAny = globalThis?.crypto;
    if (typeof cryptoAny?.randomUUID === "function") return cryptoAny.randomUUID();
  } catch {
  }
  return `view_${Math.random().toString(16).slice(2)}_${Date.now().toString(16)}`;
}
function listSysmlQueryViews(storage = getDefaultStorage()) {
  const views = safeParseViews(storage.getItem(STORAGE_KEY));
  return views.sort((a, b) => (b.updatedAt || b.createdAt).localeCompare(a.updatedAt || a.createdAt));
}
function createSysmlQueryView(name, query, storage = getDefaultStorage()) {
  const trimmedName = name.trim();
  const trimmedQuery = query.trim();
  if (!trimmedName) throw new Error("View name is required");
  if (!trimmedQuery) throw new Error("View query is required");
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const view = {
    id: createId(),
    name: trimmedName,
    query: trimmedQuery,
    createdAt: now,
    updatedAt: now
  };
  const next = [view, ...safeParseViews(storage.getItem(STORAGE_KEY))];
  writeViews(storage, next);
  return view;
}
function updateSysmlQueryView(id, patch, storage = getDefaultStorage()) {
  const views = safeParseViews(storage.getItem(STORAGE_KEY));
  const idx = views.findIndex((v) => v.id === id);
  if (idx === -1) return null;
  const now = (/* @__PURE__ */ new Date()).toISOString();
  const next = [...views];
  const current = next[idx];
  next[idx] = {
    ...current,
    name: patch.name != null ? patch.name.trim() : current.name,
    query: patch.query != null ? patch.query.trim() : current.query,
    updatedAt: now
  };
  if (!next[idx].name) return null;
  if (!next[idx].query) return null;
  writeViews(storage, next);
  return next[idx];
}
function deleteSysmlQueryView(id, storage = getDefaultStorage()) {
  const views = safeParseViews(storage.getItem(STORAGE_KEY));
  const next = views.filter((v) => v.id !== id);
  if (next.length === views.length) return false;
  writeViews(storage, next);
  return true;
}
export {
  DEFAULT_LAYOUT_OPTIONS,
  computeElkLayout,
  createElkLayout,
  createSysmlQueryView,
  deleteSysmlQueryView,
  edgeSectionsToSvgPath,
  getActivityCandidates,
  getDefaultStorage,
  getEdgeLabelPosition,
  getIBDCandidates,
  getPackageCandidates,
  getParametricCandidates,
  getRequirementCandidates,
  getSequenceCandidates,
  getStateMachineCandidates,
  getUseCaseCandidates,
  listSysmlQueryViews,
  projectActivityDiagrams,
  projectIBD,
  projectPackageDiagrams,
  projectParametricDiagrams,
  projectRequirementDiagrams,
  projectSequenceDiagrams,
  projectStateMachineDiagrams,
  projectSysmlDiagram,
  projectUseCaseDiagrams,
  updateSysmlQueryView
};
//# sourceMappingURL=index.js.map